import { Link } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { StatCard, StatusBadge } from '@/components/admin/AdminShared';

export default function AdminDashboard() {
  const { data: courses   = [] } = trpc.courses.listMine.useQuery();
  const { data: sessions  = [] } = trpc.sessions.listAll.useQuery();
  const { data: bookings  = [] } = trpc.bookings.list.useQuery();
  const { data: purchases = [] } = trpc.purchases.list.useQuery();

  const pendingBookings = bookings.filter(b => b.status === 'pending').length;
  const totalRevenue    = purchases
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + parseFloat(p.pricePaid as string), 0);

  const recentBookings  = bookings.slice(0, 5);
  const recentPurchases = purchases.slice(0, 5);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white tracking-tight">Dashboard</h1>
        <p className="text-[#E4E4E7] text-lg mt-1">Overview of your platform</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard label="Courses"  value={courses.length}   color="#FBBF24" />
        <StatCard label="Sessions" value={sessions.length}  color="#60a5fa" />
        <StatCard
          label="Bookings" value={bookings.length} color="#4ade80"
          sub={pendingBookings > 0 ? `${pendingBookings} pending` : undefined} />
        <StatCard label="Revenue"  value={`${totalRevenue} KD`} color="#e879f9"
                  sub={`${purchases.length} orders`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">

        {/* Recent bookings */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Recent Bookings</h2>
            <Link to="/admin/bookings"
                  className="text-yellow-400 hover:text-yellow-300 text-sm font-semibold transition-colors">
              View all →
            </Link>
          </div>
          <div className="space-y-2">
            {recentBookings.length === 0 && (
              <div className="mp-card p-6 text-center">
                <p className="text-[#52525B]">No bookings yet</p>
              </div>
            )}
            {recentBookings.map(b => (
              <div key={b.id} className="mp-card px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-white font-semibold text-sm truncate">
                      {(b as any).user?.name ?? '—'}
                    </span>
                    <StatusBadge status={b.status} />
                  </div>
                  <p className="text-[#71717A] text-xs truncate">{b.topic}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[#E4E4E7] text-xs">{(b as any).session?.date ?? '—'}</p>
                  <p className="text-[#52525B] text-xs">{(b as any).session?.time ?? ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent orders */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Recent Orders</h2>
            <Link to="/admin/orders"
                  className="text-yellow-400 hover:text-yellow-300 text-sm font-semibold transition-colors">
              View all →
            </Link>
          </div>
          <div className="space-y-2">
            {recentPurchases.length === 0 && (
              <div className="mp-card p-6 text-center">
                <p className="text-[#52525B]">No orders yet</p>
              </div>
            )}
            {recentPurchases.map(p => (
              <div key={p.id} className="mp-card px-4 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-white font-semibold text-sm truncate">
                      {(p as any).user?.name ?? '—'}
                    </span>
                    <StatusBadge status={p.status} />
                  </div>
                  <p className="text-[#71717A] text-xs truncate">
                    {(p as any).course?.title ?? '—'}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-yellow-400 font-bold text-sm">{p.pricePaid} KD</p>
                  <p className="text-[#52525B] text-xs">
                    {new Date(p.purchaseDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
